import {AssigneePickerViewerGraphqlQuery} from '@github-ui/item-picker/AssigneePicker'

import {useCallback, useEffect} from 'react'
import {useIssueCreateDataContext} from '../contexts/IssueCreateDataContext'
import {useQueryLoader, type PreloadedQuery} from 'react-relay'
import {useIssueCreateConfigContext} from '../contexts/IssueCreateConfigContext'
import {MilestonesPickerGraphqlQuery, type Milestone} from '@github-ui/item-picker/MilestonePicker'
import type {MilestonePickerQuery} from '@github-ui/item-picker/MilestonePickerQuery.graphql'
import {LabelPickerGraphqlQuery} from '@github-ui/item-picker/LabelPicker'
import type {LabelPickerQuery} from '@github-ui/item-picker/LabelPickerQuery.graphql'
import {ProjectPickerGraphqlQuery} from '@github-ui/item-picker/ProjectPicker'
import type {ProjectPickerQuery} from '@github-ui/item-picker/ProjectPickerQuery.graphql'
import type {AssigneePickerViewerQuery} from '@github-ui/item-picker/AssigneePickerViewerQuery.graphql'
import {MetadataFooter} from './MetadataFooter'
import {MetadataSidebar} from './MetadataSidebar'

export type SharedMetadataProps = {
  // Optional for the viewerRef as it's only needed for the sidebar
  viewerRef?: PreloadedQuery<AssigneePickerViewerQuery> | null | undefined
  labelsRef: PreloadedQuery<LabelPickerQuery> | null | undefined
  milestonesRef: PreloadedQuery<MilestonePickerQuery> | null | undefined
  projectsRef: PreloadedQuery<ProjectPickerQuery> | null | undefined
  setMilestoneCallback: (milestones: Milestone[] | null) => void
  canAssign: boolean
  canLabel: boolean
  canMilestone: boolean
  canProject: boolean
}

export const MetadataSelectors = () => {
  const {optionConfig} = useIssueCreateConfigContext()
  const showMetadataInSidebar = !optionConfig.insidePortal
  const {repository, setMilestone} = useIssueCreateDataContext()

  const [viewerRef, loadViewer, disposeViewer] = useQueryLoader<AssigneePickerViewerQuery>(
    AssigneePickerViewerGraphqlQuery,
  )
  const [labelsRef, loadLabels, disposeLabels] = useQueryLoader<LabelPickerQuery>(LabelPickerGraphqlQuery)
  const [projectsRef, loadProjects, disposeProjects] = useQueryLoader<ProjectPickerQuery>(ProjectPickerGraphqlQuery)
  const [milestonesRef, loadMilestones, disposeMilestones] =
    useQueryLoader<MilestonePickerQuery>(MilestonesPickerGraphqlQuery)

  useEffect(() => {
    if (repository) {
      if (showMetadataInSidebar) {
        loadViewer({}, {fetchPolicy: 'store-or-network'})
      }
      loadLabels({owner: repository.owner.login, repo: repository.name}, {fetchPolicy: 'store-or-network'})
      loadProjects({owner: repository.owner.login, repo: repository.name}, {fetchPolicy: 'store-or-network'})
      loadMilestones({owner: repository.owner.login, repo: repository.name}, {fetchPolicy: 'store-or-network'})
    }
    return () => {
      disposeViewer()
      disposeLabels()
      disposeProjects()
      disposeMilestones()
    }
  }, [
    repository,
    disposeLabels,
    disposeMilestones,
    disposeProjects,
    disposeViewer,
    loadLabels,
    loadMilestones,
    loadProjects,
    loadViewer,
    showMetadataInSidebar,
  ])

  const setMilestoneCallback = useCallback(
    (milestones: Milestone[] | null) => {
      setMilestone(milestones?.[0] || null)
    },
    [setMilestone],
  )

  const canAssign = repository?.viewerIssueCreationPermissions?.assignable
  const canLabel = repository?.viewerIssueCreationPermissions?.labelable
  const canMilestone = repository?.viewerIssueCreationPermissions?.milestoneable
  const canProject = repository?.viewerIssueCreationPermissions?.triageable

  const sharedMetadataProps: SharedMetadataProps = {
    viewerRef: showMetadataInSidebar ? viewerRef : undefined,
    labelsRef,
    milestonesRef,
    projectsRef,
    setMilestoneCallback,
    canAssign: canAssign ?? false,
    canLabel: canLabel ?? false,
    canMilestone: canMilestone ?? false,
    canProject: canProject ?? false,
  }

  return showMetadataInSidebar ? (
    <MetadataSidebar {...sharedMetadataProps} />
  ) : (
    <MetadataFooter {...sharedMetadataProps} />
  )
}

try{ MetadataSelectors.displayName ||= 'MetadataSelectors' } catch {}